import BaseController from "confinus_ui/base_controller";
import OrderGuideRowController, {
  ProductPriceChangedEvent,
} from "./order_guide_row_controller";
import { BIG_ZERO, newPositiveBigOrNull } from "lib/price_calculations";
import Big from "big.js";
import {
  Mercor__OrderGuideRowControllerIdentifier,
  Mercor__Forms__DateCalendarControllerIdentifier,
} from "controllers/identifiers";
import OrderGuidePersistenceService, {
  OrderGuideStateInput,
} from "./order_guide_persistence_service";

export default class OrderGuideController extends BaseController {
  public static targets = ["searchField", "totalPrice", "totalPriceFooter"];
  private rows: { [key: string]: Big } = {};
  declare private readonly searchFieldTarget: HTMLFormElement;
  declare private readonly totalPriceTarget: HTMLFormElement;
  declare private readonly totalPriceFooterTarget: HTMLFormElement;

  public static outlets = [Mercor__OrderGuideRowControllerIdentifier];
  declare private readonly mercorOrderGuideRowOutlets: OrderGuideRowController[];
  private get rowControllers() {
    return this.mercorOrderGuideRowOutlets;
  }

  public static values = {
    orderGuideId: String,
  };
  declare private readonly orderGuideIdValue: string;

  private sortDirection: { [key: string]: boolean } = {};

  private debounceSearch?: any = undefined;
  private debouncePersist?: any = undefined;

  onInitialize() {
    this.rows = {};

    this.element.addEventListener("cnf:delivery-date-selected", () => {
      this.schedulePersist();
    });

    setTimeout(() => this.restorePersistedState(), 0);

    return super.onInitialize();
  }

  public submittedForm(evt: Event) {
    OrderGuidePersistenceService.clear(this.orderGuideIdValue);
  }

  public sortBySku() {
    this.applySort("sku");
  }

  public sortByDescription() {
    this.applySort("description");
  }

  public search(evt: Event) {
    const val = this.searchFieldTarget.value.trim().toLowerCase();

    if (this.debounceSearch) {
      clearTimeout(this.debounceSearch);
    }
    this.debounceSearch = setTimeout(() => {
      this.debounceSearch = undefined;

      const tbodies = this.element.querySelectorAll<HTMLElement>(
        "tbody[data-product-sku]",
      );
      tbodies.forEach((tbody) => {
        if (val.length < 2 || val === "") {
          tbody.classList.remove("hidden");
          return;
        }
        const sku = (
          tbody.getAttribute("data-product-sku") || ""
        ).toLowerCase();
        const desc = (
          tbody.getAttribute("data-product-description") || ""
        ).toLowerCase();
        const matches = sku.includes(val) || desc.includes(val);
        tbody.classList.toggle("hidden", !matches);
      });
    }, 300);
  }

  public handleRowPriceChange(evt: ProductPriceChangedEvent) {
    const { totalPriceNumber, sku, variant } = evt.detail;
    const price = newPositiveBigOrNull(totalPriceNumber) || BIG_ZERO;
    this.rows[`${sku}:${variant}`] = price;
    const estimate = Object.values(this.rows).reduce<Big>(
      (a: Big, b: Big) => a.plus(b),
      BIG_ZERO,
    );
    this.updateTotalPrice(estimate);
    this.schedulePersist();
  }

  private schedulePersist() {
    if (this.debouncePersist) {
      clearTimeout(this.debouncePersist);
    }
    this.debouncePersist = setTimeout(() => {
      this.debouncePersist = undefined;
      this.persistState();
    }, 500);
  }

  private persistState() {
    const rows: OrderGuideStateInput["rows"] = {};
    this.rowControllers.forEach((rowController: OrderGuideRowController) => {
      const state = rowController.getState();
      if (state) {
        rows[`${state.sku}:${state.variant}`] = {
          quantity: state.quantity,
          size: state.size,
        };
      }
    });

    const deliveryDate = this.getDeliveryDateValue();

    if (Object.keys(rows).length === 0 && !deliveryDate) {
      OrderGuidePersistenceService.clear(this.orderGuideIdValue);
      return;
    }

    OrderGuidePersistenceService.save(this.orderGuideIdValue, {
      deliveryDate,
      rows,
    });
  }

  private restorePersistedState() {
    const state = OrderGuidePersistenceService.load(this.orderGuideIdValue);
    if (!state) return;

    if (state.deliveryDate) {
      this.restoreDeliveryDate(state.deliveryDate);
    }

    if (state.rows) {
      this.rowControllers.forEach((rowController: OrderGuideRowController) => {
        const key = `${rowController.sku}:${rowController.variantEncodedId}`;
        const rowState = state.rows[key];
        if (rowState) {
          rowController.restoreState(rowState);
        }
      });
    }
  }

  private getDeliveryDateValue(): string | undefined {
    const input = this.element.querySelector<HTMLInputElement>(
      'input[name*="[delivery_date]"]',
    );
    return input?.value || undefined;
  }

  private restoreDeliveryDate(dateStr: string) {
    // DateCalendar moved from ConfinusUI to Mercor — uses the same flatpickr
    // controller, now under the mercor--forms--date-calendar identifier.
    const dateCalendarEl = this.element.querySelector(
      `[data-controller*="${Mercor__Forms__DateCalendarControllerIdentifier}"]`,
    );
    if (dateCalendarEl) {
      const app = (this as any).application;
      const controller = app.getControllerForElementAndIdentifier(
        dateCalendarEl,
        Mercor__Forms__DateCalendarControllerIdentifier,
      );
      if (controller && typeof controller.setDate === "function") {
        controller.setDate(dateStr, true);
      }
    }
  }

  private applySort(parameter: string) {
    if (!this.sortDirection.hasOwnProperty(parameter)) {
      this.sortDirection[parameter] = false;
    }

    const table = this.element.querySelector("table")!;
    const tbodies = Array.from(
      table.querySelectorAll<HTMLElement>("tbody[data-product-sku]"),
    );

    tbodies.forEach((tbody) => tbody.remove());

    tbodies.sort((a, b) => {
      const aVal = this.getSortValue(a, parameter);
      const bVal = this.getSortValue(b, parameter);
      return this.sortDirection[parameter]
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    tbodies.forEach((tbody) => table.appendChild(tbody));
    this.sortDirection[parameter] = !this.sortDirection[parameter];
  }

  private getSortValue(tbody: Element, parameter: string): string {
    switch (parameter) {
      case "sku":
        return (tbody.getAttribute("data-product-sku") || "").toLowerCase();
      case "description":
        return (
          tbody.getAttribute("data-product-description") || ""
        ).toLowerCase();
      default:
        return "";
    }
  }

  private updateTotalPrice(price: any) {
    this.totalPriceFooterTarget.textContent =
      this.totalPriceTarget.textContent = `$ ${price.toFixed(2)}`;
  }
}
